import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { PaginatedResponse } from "..";
import { ByDistanceQuery, PaginatedQueryParams } from "../types";
import { Locateable, useAddUserLocationToQuery } from "./locateable";
import { ImageMedia, Media } from "./Media";
import {
  fetchPaginatedQueryFunction,
  fetchQueryFunction,
  makeQueryKey,
  PaginatedQueryFunction,
  useInfiniteQuery,
  UseInfiniteQueryFunction,
  usePaginatedQuery,
  UsePaginatedQueryFunction,
} from "./queries";
import { Tag, TagFilterParams } from "./Tags";

type ItineraryListItem = {
  id: number;
  name: string;
  lead_media?: Media;
  slug: string;
  uuid: string;
};

export type ItineraryCtaReturnType = HasItineraryCTA | NoItineraryCta;

type HasItineraryCTA = ItineraryListItem & {
  id: number;
  type: "new" | "continue";
};

type NoItineraryCta = { id: number; type: "none" };

type FeaturedItinerary = ItineraryListItem & { lead_media?: Media };

type StopTypeCount = { type: string; name: string; count: number };

export type BriefItinerary = ItineraryListItem & {
  featured: boolean;
  lead_media?: Media;
  thumbnail_media?: ImageMedia;
  stop_count: number;
  status: string;
  subtitle?: string;
  short_description?: string;
  show_map: boolean;
  leg_count: number;
  cities: string[];
  stop_type_counts: StopTypeCount[];
  tags: Tag[];
};

export type Dmo = {
  id: number;
  name: string;
  logo_url: string;
  website: string;
};
/** CMS blocks still returned on some itinerary payloads. */
export type ItineraryContentBlock = {
  id: number;
  content_type_name: string;
  title?: string | null;
  description?: string | null;
  images?: { url: string; caption?: string }[];
};

export type Itinerary = BriefItinerary & {
  description: string;
  subtitle?: string;
  slug: string;
  main_content: string; //deprecated use description
  short_description?: string;
  area: { latitude: number; longitude: number; latitudeDelta: number };
  location?: Locateable;
  region_id: number;
  region_name: string;
  legs: Leg[];
  tags: Tag[];
  album_media: Media[];
  dmos: Dmo[];
  contents?: ItineraryContentBlock[];
};

export type Leg = {
  id: number;
  order_on_itin: number;
  title: string;
  name: string;
  description?: string;
  stops: ItineraryStop[];
  lead_media?: Media;
};

type BasicStop = {
  id: number;
  type: string;
  order_on_leg: number;
  name: string;
  description: string;
  checkinable: boolean;
  main_type: string;
  city: string;
  full_address?: string;
  lead_media?: Media;
  status: string;
  target_slug?: string;
  website?: string;
  website2?: string;
  subtitle?: string;
  short_description?: string;
} & (
  | { target_type: "Region" | "Listing" | "Event"; target_id: number }
  | { target_type: null; target_id: null }
);

export type LocateableStop = BasicStop & {
  display_type: "step";
  subtype?: string;
  latlng: { latitude: number; longitude: number };
  lonlat: string;
  stop_number: number;
  total_stop_number: number;
  main_type: string;
  polyline: string;
};

export type NoteStop = BasicStop & {
  display_type: "note";
  total_stop_number: null;
  stop_number: null;
  lonlat?: null;
};

export type ItineraryStop = LocateableStop | NoteStop;

export const isLeg = (item?: ItineraryStop | Leg): item is Leg => {
  return !!item && Object.keys(item).includes("order_on_itin");
};

export const isNoteStop = (stop: ItineraryStop): stop is NoteStop => {
  return stop.display_type === "note";
};

export const isLocateableStop = (
  stop: ItineraryStop,
): stop is LocateableStop => {
  return !!stop.lonlat;
};

// TODO: Check if "isTargetStop" is needed
export const isTargetStop = (stop: ItineraryStop): stop is LocateableStop => {
  return stop.target_type !== null;
};

export const isListingStop = (
  stop: ItineraryStop,
): stop is LocateableStop & { target_type: "Listing" } =>
  isLocateableStop(stop) && stop.target_type === "Listing";

export const findLegIndexForStop = (legs: Leg[], stopId: number) =>
  legs.findIndex((leg) => leg.stops.some((stop) => stop.id === stopId));

export const fetchItinerary = (id: number | string) =>
  fetchQueryFunction<void, Itinerary>({
    path: `itineraries/${id}`,
    resource: "itineraries",
    id,
  });

export const useItinerary = (
  id: number | string,
  options?: Omit<UseQueryOptions<Itinerary, Error>, "queryKey" | "queryFn">,
) =>
  useQuery<Itinerary, Error>({
    queryKey: makeQueryKey({ resource: "itineraries", id }),
    queryFn: () => fetchItinerary(id),
    ...options,
  });

// export const fetchItinerary = async (id: number | string) => {
//   const { data } = await apiClient().get<Itinerary>(`itineraries/${id}`);
//   return data;
// };

// export const useItinerary = (id: number | string) =>
//   useQuery<Itinerary, Error>({
//     queryKey: ["itineraries", id],
//     queryFn: () => fetchItinerary(id),
//   });

export type ItinerarySortOptionKeys =
  | "featured"
  | "alpha"
  | "distance"
  | "stop_count"
  | "popular";

export const ItinerarySortOptions: {
  [key in ItinerarySortOptionKeys]: string;
} = {
  featured: "Featured",
  stop_count: "Number of Stops (least to most)",
  alpha: "Name (A-Z)",
  distance: "Distance (nearest first)",
  popular: "Most Popular",
};

export type ItineraryQueryParams = ByDistanceQuery &
  PaginatedQueryParams & {
    sort?: ItinerarySortOptionKeys;
    search?: string;
    progress?: string;
    filter?: ItineraryFilterParams;
    excluded_id?: number | number[]; // TODO: Change to exclude IDs
  };

export type ItineraryFilterParams = {
  tags?: TagFilterParams;
  region_id?: number | number[];
  featured?: boolean;
};

export type ItineraryResponse = PaginatedResponse<BriefItinerary>;

export const fetchItineraries: PaginatedQueryFunction<
  ItineraryQueryParams,
  ItineraryResponse
> = (params, preload) =>
  fetchPaginatedQueryFunction({
    path: "itineraries",
    resource: "itineraries",
    params,
    preload,
  });

export const useItineraries: UsePaginatedQueryFunction<
  BriefItinerary,
  ItineraryQueryParams
> = (params, options) => {
  const { params: locationParams, enabled } = useAddUserLocationToQuery(params);
  return usePaginatedQuery("itineraries", fetchItineraries, locationParams, {
    ...options,
    enabled: enabled && options?.enabled,
  });
};

export const useInfiniteItineraries: UseInfiniteQueryFunction<
  BriefItinerary,
  ItineraryQueryParams
> = (params, options) => {
  const { params: locationParams, enabled } = useAddUserLocationToQuery(params);
  return useInfiniteQuery("itineraries", fetchItineraries, locationParams, {
    ...options,
    enabled: enabled && options?.enabled,
  });
};

const fetchItineraryCta = () =>
  fetchQueryFunction<void, ItineraryCtaReturnType>({
    path: "itineraries/cta",
    resource: ["itinerary-cta"],
  });

export const useItineraryCta = () =>
  useQuery<ItineraryCtaReturnType, Error>({
    queryKey: ["itinerary-cta"],
    queryFn: fetchItineraryCta,
  });

const fetchFeaturedItinerary = () =>
  fetchQueryFunction<void, FeaturedItinerary>({
    path: "itineraries/featured",
    resource: ["itinerary-featured"],
  }).catch((error) => {
    if (error.response.status === 404) {
      return null;
    } else {
      throw new Error(error);
    }
  });

export const useFeaturedItinerary = () =>
  useQuery<FeaturedItinerary | null, Error>({
    queryKey: ["itinerary-featured"],
    queryFn: fetchFeaturedItinerary,
  });
